import React from "react"
import { useLocation, useNavigate } from "react-router";

import { BookOpen, House, Menu, Monitor, X } from "lucide-react";
import kakaoChannelIcon from "../../assets/kakaotalk_sharing_btn_small.svg";
import kakaoChannelHoverIcon from "../../assets/kakaotalk_sharing_btn_small_ov.svg";

import { useAuth } from "../../features/auth/AuthContext"
import { AuthContextType } from "../../features/auth/AuthContext";
import { useWebSocket } from "../providers/WebSocketContext"
import { ItemProvider, useItemContext } from "../../features/items/model/ItemContext"
import ProcessLogSidebar from "../../features/process-records/components/ProcessLogSidebar";
import { ThemeToggle } from "../../shared/ui/ThemeToggle";
import { ZoomControl } from "../../shared/ui/ZoomControl";
import { API_UNREACHABLE_MESSAGE } from "../../shared/api/helpers";
import type { ApiErrorSetter } from "../../shared/api/helpers";


interface LayoutProps {
  children: React.ReactNode
}

const EnvironmentBadge = () => {
  const environment = import.meta.env.VITE_APP_ENV;
  if (environment !== "LOCAL" && environment !== "DEV") {
    return null;
  }

  return (
    <span className={`badge brand-environment-badge brand-environment-${environment.toLowerCase()}`}>
      {environment}
    </span>
  );
};

const LayoutContent: React.FC<LayoutProps> = ({ children }) => {

  // 훅스
  const auth = useAuth();
  const authRef = React.useRef(auth);
  React.useEffect(() => {
    authRef.current = auth;
  }, [auth]);
  const navigate = useNavigate();
  const location = useLocation();
  const previousPath = React.useRef(location.pathname);
  React.useEffect(() => {
    if (previousPath.current !== location.pathname) {
      previousPath.current = location.pathname;
      void authRef.current.refreshSubscriptionStatus();
    }
  }, [location.pathname]);
  React.useEffect(() => {
    const refreshOnFocus = () => {
      if (authRef.current.isAuthenticated) void authRef.current.refreshSubscriptionStatus();
    };
    window.addEventListener("focus", refreshOnFocus);
    return () => window.removeEventListener("focus", refreshOnFocus);
  }, []);
  const webSocket = useWebSocket();
  const {
    notifyItemProcess,
    addProcessLog,
    fetchProcessLogs,
    fetchSingleItem,
    updateItem,
  } = useItemContext();

  // 스테이트: UI
  const [collapseShow, setCollapseShow] = React.useState<boolean>(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState<boolean>(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 840px)").matches,
  );
  const [isUserRunning, setIsUserRunning] = React.useState<boolean>(false);
  const hasConnectedWebSocketRef = React.useRef<boolean>(false);

  // 스테이트: 데이터
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [processLogError, setProcessLogError] = React.useState<string>("");
  const [infraBannerDismissed, setInfraBannerDismissed] = React.useState<boolean>(false);
  const { processLogs } = useItemContext();
  const infraNoticeMessage = auth.processReadiness?.ready === false
    ? auth.processReadiness.errors[0]?.message
    : null;
  const setProcessLogErrorMessage = React.useCallback<ApiErrorSetter>((message, isConnectionFailure = false) => {
    if (isConnectionFailure) {
      setProcessLogError("");
      void authRef.current.refreshProcessReadiness();
      return;
    }
    setProcessLogError(message);
  }, []);

  // 이펙트: 데이터 셋업
  React.useEffect(() => {
    fetchProcessLogs(auth, setProcessLogErrorMessage);
  }, []);

  // 이펙트: 웹소켓 재연결 후 작업이력 보완
  React.useEffect(() => {
    if (!webSocket.isConnected) {
      return;
    }

    if (hasConnectedWebSocketRef.current) {
      fetchProcessLogs(authRef.current, setProcessLogErrorMessage);
    }
    hasConnectedWebSocketRef.current = true;
  }, [webSocket.isConnected, fetchProcessLogs, setProcessLogErrorMessage]);

  React.useEffect(() => {
    const mobileViewport = window.matchMedia("(max-width: 840px)");
    const collapseOnMobile = (event: MediaQueryListEvent) => {
      if (event.matches) setSidebarCollapsed(true);
    };
    mobileViewport.addEventListener("change", collapseOnMobile);
    return () => mobileViewport.removeEventListener("change", collapseOnMobile);
  }, []);

  React.useEffect(() => {
    setInfraBannerDismissed(false);
  }, [infraNoticeMessage]);

  // 이펙트: 스테이트 변경
  React.useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 5000); // 5초 후 자동 해제
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // 이펙트: 웹소켓 (lastMessage 변경 시에만 1회 처리; auth/토글은 ref로 읽음)
  React.useEffect(() => {
    if (!webSocket.lastMessage) {
      return;
    }
    const message = webSocket.lastMessage;

    // 아이템 업데이트
    if (message.item_id) {
      if (message.category === 'item_process_started') {
        updateItem(message.item_id, { is_processing: true });
      }
      if (message.category === 'item_process' && 'floor_price_reached_snapshot' in message) {
        updateItem(message.item_id, {
          floor_price_reached_snapshot: message.floor_price_reached_snapshot,
        });
      }
      // 아이템 정보 새로고침
      fetchSingleItem(message.item_id, authRef.current);
    }

    // 실행 상태 변경: 서버 WebSocket만 작업이력에 추가해 수동 전환과 중복을 피함
    if (message.category === "is_running_true" || message.category === "is_running_false") {
      setIsUserRunning(message.category === "is_running_true");
      addProcessLog(message);
    }

    // 아이템 프로세스 완료/오류: 로그·행 하이라이트·토스트
    if (
      (message.started_at && message.finished_at) ||
      message.category === "item_process_error"
    ) {
      notifyItemProcess(message);
    }
  }, [webSocket.lastMessage, addProcessLog, fetchSingleItem, updateItem, notifyItemProcess])

  const handleLogout = () => {
    auth.logout();
  };

  const navItems = [
    { path: "/items/", label: "상품목록", matches: ["/", "/items/"] },
    { path: "/itemgroups/", label: "상품그룹" },
    { path: "/records/", label: "작업이력" },
    { path: "/userconfig/", label: "환경설정" },
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
    setCollapseShow(false);
  };

  return (
    <>
      <header className="topnav">
        <button type="button" className="brand" onClick={() => handleNavigate("/items/")}>
          <span className="brand-dot" aria-hidden="true" />
          nprice
          <EnvironmentBadge />
        </button>
        {auth.isAuthenticated && (
          <>
            <button
              type="button"
              className="mobile-menu-button"
              aria-expanded={collapseShow}
              aria-controls="primary-navigation"
              onClick={() => setCollapseShow((show) => !show)}
            >
              {collapseShow ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
              <span className="visually-hidden">메뉴</span>
            </button>
            <nav id="primary-navigation" className={`nav-links ${collapseShow ? "open" : ""}`}>
              {navItems.map((item) => {
                const isActive = Boolean(
                  item.matches?.includes(location.pathname) || location.pathname.startsWith(item.path),
                );
                return (
                  <button
                    key={item.path}
                    type="button"
                    className={`nav-link ${isActive ? "active" : ""}`}
                    onClick={() => handleNavigate(item.path)}
                  >
                    {item.label}
                  </button>
                );
              })}
              <div className="nav-icon-links">
                <a
                  className="nav-link nav-link-with-icon"
                  href={import.meta.env.VITE_APP_ENV === "DEV" ? "https://www-dev.nprice.app" : "https://www.nprice.app"}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <House aria-hidden="true" />
                  홈페이지
                </a>
                <a
                  className="nav-link nav-link-with-icon"
                  href="https://console.nprice.app"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Monitor aria-hidden="true" />
                  웹콘솔
                </a>
                <a
                  className="nav-link nav-link-with-icon"
                  href="https://nprice-app.netlify.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <BookOpen aria-hidden="true" />
                  사용설명서
                </a>
                <a
                  className="nav-link nav-link-with-icon"
                  href="http://helppc.net/nprice"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="원격지원"
                  title="원격지원"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 13a8 8 0 0 1 16 0"/><path d="M4 13v4a2 2 0 0 0 2 2h1v-6H4Z"/><path d="M20 13v4a2 2 0 0 1-2 2h-1v-6h3Z"/><path d="M15 19h-3"/></svg>
                  원격지원
                </a>
                <a
                  className="nav-link nav-link-with-icon kakao-channel-link"
                  href="https://pf.kakao.com/_xixaQFX"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img className="kakao-channel-icon" src={kakaoChannelIcon} alt="" />
                  <img className="kakao-channel-icon-hover" src={kakaoChannelHoverIcon} alt="" />
                  카카오톡
                </a>
              </div>
            </nav>
            <div className="topnav-right">
              <ZoomControl />
              <ThemeToggle />
              <SubscriptionStatusDisplay auth={auth} />
              <button type="button" className="logout-link" onClick={handleLogout}>로그아웃</button>
            </div>
          </>
        )}
      </header>

      <div className={`app-shell ${sidebarCollapsed ? "sidebar-collapsed" : "sidebar-expanded"}`}>
        <main className="main-content">
          {/* django API 연결 실패 — 복구 전까지 dismiss 불가 */}
          {auth.apiReachable === false && (
            <div className="alert alert-danger fade show" role="alert">
              {API_UNREACHABLE_MESSAGE}
            </div>
          )}
          {/* 메인: 인프라 미준비 안내 */}
          {infraNoticeMessage && !infraBannerDismissed && (
            <div className="alert alert-warning alert-dismissible fade show" role="alert">
              {infraNoticeMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setInfraBannerDismissed(true)}
                aria-label="닫기"
              ></button>
            </div>
          )}
          {/* 메인: 알럿 메시지 */}
          {errorMessage && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
                aria-label="닫기"
              ></button>
            </div>
          )}
          {/* 메인: 컨텐츠 */}
          {children}
        </main>

      {auth.isAuthenticated && (
        <ProcessLogSidebar
          auth={auth}
          processLogs={processLogs}
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
          isUserRunning={isUserRunning}
          onIsUserRunningChange={setIsUserRunning}
          error={processLogError}
          onErrorMessage={setErrorMessage}
        />
      )}
      </div>
    </>
  )
}

const AppLayout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <ItemProvider>
      <LayoutContent>{children}</LayoutContent>
    </ItemProvider>
  )
}

const SubscriptionStatusDisplay = ({ auth }: { auth: AuthContextType }) => {
  if (!auth.isAuthenticated || auth.isSubscriptionLoading) {
    return null;
  }

  //console.log('SubscriptionStatus:', auth.subscriptionStatus);  // 디버그 로그

  const remainingDays = auth.subscriptionStatus?.remaining_days || 0;
  const premiumBadge = auth.subscriptionStatus?.item_is_premium ? <span className="badge text-bg-primary">프로</span> : <></>;
  const expiredAt = auth.subscriptionStatus?.expired_at;
  const expiryDate = expiredAt ? new Date(expiredAt) : null;
  const expiryTitle = expiryDate && !Number.isNaN(expiryDate.getTime())
    ? `만료: ${new Intl.DateTimeFormat("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(expiryDate)}`
    : undefined;

  return (
    <span>
      {auth.getUsername()}님<small> (잔여기간: {premiumBadge} <span title={expiryTitle}>{remainingDays}일</span>)</small>
    </span>
  );
}


export default AppLayout;
